import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Zap, Plus, Loader2, Play, Pause, Trash2, Edit3, X, AlertTriangle, Bell, TrendingUp, TrendingDown, DollarSign, CheckCircle, Target, Clock, Calendar, Filter, ChevronDown, ChevronUp } from 'lucide-react';

const Automations = () => {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', isActive: true,
    conditions: { metric: 'cpa', operator: '>', value: '', period: 'last_3d' },
    actions: { type: 'pause', target: 'campaign', budgetPercent: 20, budgetValue: 0, notifyEmail: true, notifyPush: true },
    targetFilter: { type: 'all_active', nameContains: '', campaignId: '' },
    schedule: { enabled: false, days: [1,2,3,4,5], startHour: '00:00', endHour: '23:59' }
  });

  const metrics = [
    { value: 'cpa', label: 'CPA (Custo por Aquisição)' },
    { value: 'cpl', label: 'CPL (Custo por Lead)' },
    { value: 'roas', label: 'ROAS (Retorno sobre Gasto)' },
    { value: 'ctr', label: 'CTR (Taxa de Cliques)' },
    { value: 'cpm', label: 'CPM (Custo por Mil)' },
    { value: 'spend', label: 'Gasto Total' },
    { value: 'impressions', label: 'Impressões' },
    { value: 'clicks', label: 'Cliques' },
    { value: 'conversions', label: 'Conversões' },
    { value: 'messages', label: 'Mensagens Iniciadas' },
    { value: 'cost_per_message', label: 'Custo por Mensagem' },
    { value: 'frequency', label: 'Frequência' },
    { value: 'whatsapp_health', label: 'Saúde WhatsApp (inapto)' },
    { value: 'reach', label: 'Alcance' },
    { value: 'whatsapp_quality', label: 'Qualidade WhatsApp' },
  ];

  const operators = [
    { value: '>', label: 'Maior que' },
    { value: '<', label: 'Menor que' },
    { value: '>=', label: 'Maior ou igual' },
    { value: '<=', label: 'Menor ou igual' },
    { value: '==', label: 'Igual a' },
  ];

  const periods = [
    { value: 'today', label: 'Hoje' },
    { value: 'last_1d', label: 'Último dia' },
    { value: 'last_3d', label: 'Últimos 3 dias' },
    { value: 'last_7d', label: 'Últimos 7 dias' },
    { value: 'last_14d', label: 'Últimos 14 dias' },
    { value: 'last_30d', label: 'Últimos 30 dias' },
  ];

  const actionTypes = [
    { value: 'pause', label: 'Pausar', icon: Pause, color: 'text-red-400' },
    { value: 'activate', label: 'Ativar', icon: Play, color: 'text-green-400' },
    { value: 'increase_budget', label: 'Aumentar Orçamento (%)', icon: TrendingUp, color: 'text-emerald-400' },
    { value: 'decrease_budget', label: 'Diminuir Orçamento (%)', icon: TrendingDown, color: 'text-orange-400' },
    { value: 'increase_budget_value', label: 'Aumentar Orçamento (R$)', icon: TrendingUp, color: 'text-emerald-400' },
    { value: 'decrease_budget_value', label: 'Diminuir Orçamento (R$)', icon: TrendingDown, color: 'text-orange-400' },
    { value: 'set_budget', label: 'Definir Orçamento (R$)', icon: DollarSign, color: 'text-blue-400' },
    { value: 'notify', label: 'Apenas Notificar', icon: Bell, color: 'text-blue-400' },
  ];

  const targetLevels = [
    { value: 'campaign', label: 'Campanha' },
    { value: 'adset', label: 'Conjunto de Anúncios' },
    { value: 'ad', label: 'Anúncio' },
  ];

  const filterTypes = [
    { value: 'all_active', label: 'Todas campanhas ativas' },
    { value: 'name_contains', label: 'Nome contém...' },
    { value: 'specific', label: 'Campanha específica' },
  ];

  const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  useEffect(() => { fetchAutomations(); }, []);

  const fetchAutomations = async () => {
    setLoading(true);
    try {
      const response = await api.get('/automations');
      const items = (response.data.automations || []).map(a => ({
        ...a,
        conditions: typeof a.conditions === 'string' ? JSON.parse(a.conditions) : a.conditions,
        actions: typeof a.actions === 'string' ? JSON.parse(a.actions) : a.actions,
        targetFilter: a.targetFilter ? (typeof a.targetFilter === 'string' ? JSON.parse(a.targetFilter) : a.targetFilter) : { type: 'all_active' },
        schedule: a.schedule ? (typeof a.schedule === 'string' ? JSON.parse(a.schedule) : a.schedule) : { enabled: false, days: [1,2,3,4,5] },
      }));
      setAutomations(items);
    } catch (error) {
      console.error('Erro ao carregar automações:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        conditions: JSON.stringify(formData.conditions),
        actions: JSON.stringify(formData.actions),
        targetFilter: JSON.stringify(formData.targetFilter),
        schedule: JSON.stringify(formData.schedule),
      };
      if (editingRule) {
        await api.put('/automations/' + editingRule.id, payload);
      } else {
        await api.post('/automations', payload);
      }
      fetchAutomations();
      resetForm();
    } catch (error) {
      console.error('Erro ao salvar:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta automação?')) return;
    try { await api.delete('/automations/' + id); } catch (e) {}
    setAutomations(prev => prev.filter(a => a.id !== id));
  };

  const toggleActive = async (id, currentStatus) => {
    try { await api.put('/automations/' + id, { isActive: !currentStatus }); } catch (e) {}
    setAutomations(prev => prev.map(a => a.id === id ? { ...a, isActive: !currentStatus } : a));
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingRule(null);
    setFormData({
      name: '', description: '', isActive: true,
      conditions: { metric: 'cpa', operator: '>', value: '', period: 'last_3d' },
      actions: { type: 'pause', target: 'campaign', budgetPercent: 20, budgetValue: 0, notifyEmail: true, notifyPush: true },
      targetFilter: { type: 'all_active', nameContains: '', campaignId: '' },
      schedule: { enabled: false, days: [1,2,3,4,5], startHour: '00:00', endHour: '23:59' }
    });
  };

  const openEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name, description: rule.description, isActive: rule.isActive,
      conditions: rule.conditions || { metric: 'cpa', operator: '>', value: '', period: 'last_3d' },
      actions: rule.actions || { type: 'pause', target: 'campaign', notifyEmail: true, notifyPush: true },
      targetFilter: rule.targetFilter || { type: 'all_active' },
      schedule: rule.schedule || { enabled: false, days: [1,2,3,4,5] }
    });
    setShowModal(true);
  };

  const toggleDay = (day) => {
    setFormData(prev => {
      const days = prev.schedule.days || [];
      return { ...prev, schedule: { ...prev.schedule, days: days.includes(day) ? days.filter(d => d !== day) : [...days, day].sort() } };
    });
  };

  const getActionInfo = (type) => actionTypes.find(a => a.value === type) || actionTypes[4];
  const getMetricLabel = (val) => metrics.find(m => m.value === val)?.label || val;
  const getFilterLabel = (f) => {
    if (!f) return 'Todas ativas';
    if (f.type === 'name_contains') return `Nome contém "${f.nameContains}"`;
    if (f.type === 'specific') return 'Campanha específica';
    return 'Todas ativas';
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3"><Zap className="text-yellow-500" />Automações</h1>
          <p className="text-gray-400">Regras automáticas para campanhas, conjuntos e anúncios</p>
        </div>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500"><Plus size={18} />Nova Regra</button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-3"><div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center"><Zap className="text-blue-400" size={24} /></div><div><p className="text-gray-400 text-sm">Total de Regras</p><p className="text-2xl font-bold text-white">{automations.length}</p></div></div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-3"><div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center"><CheckCircle className="text-green-400" size={24} /></div><div><p className="text-gray-400 text-sm">Ativas</p><p className="text-2xl font-bold text-green-400">{automations.filter(a => a.isActive).length}</p></div></div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-3"><div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center"><Bell className="text-purple-400" size={24} /></div><div><p className="text-gray-400 text-sm">Execuções</p><p className="text-2xl font-bold text-purple-400">{automations.reduce((sum, a) => sum + (a.triggerCount || 0), 0)}</p></div></div>
        </div>
      </div>

      {/* Lista */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-blue-500" size={40} /></div>
        ) : automations.length === 0 ? (
          <div className="text-center py-20"><Zap size={48} className="mx-auto text-gray-600 mb-4" /><p className="text-gray-400 mb-4">Nenhuma automação configurada</p><button onClick={() => setShowModal(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500">Criar primeira regra</button></div>
        ) : (
          <div className="divide-y divide-gray-700">
            {automations.map((rule) => {
              const actionInfo = getActionInfo(rule.actions?.type);
              const ActionIcon = actionInfo.icon;
              return (
                <div key={rule.id} className={`p-4 hover:bg-gray-700/30 transition-colors ${!rule.isActive ? 'opacity-60' : ''}`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${rule.isActive ? 'bg-yellow-500/20' : 'bg-gray-700'}`}>
                      <Zap className={rule.isActive ? 'text-yellow-400' : 'text-gray-500'} size={24} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-medium">{rule.name}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${rule.isActive ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>{rule.isActive ? 'Ativa' : 'Inativa'}</span>
                        {rule.actions?.target && rule.actions.target !== 'campaign' && <span className="px-2 py-0.5 rounded-full text-xs bg-purple-500/20 text-purple-400">{rule.actions.target === 'adset' ? 'Conjunto' : 'Anúncio'}</span>}
                      </div>
                      <p className="text-gray-400 text-sm mb-3">{rule.description}</p>
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-700/50 rounded-lg">
                          <AlertTriangle size={14} className="text-orange-400" />
                          <span className="text-gray-300">{getMetricLabel(rule.conditions?.metric)} {rule.conditions?.operator} {rule.conditions?.value}</span>
                        </div>
                        <div className={`flex items-center gap-2 px-3 py-1.5 bg-gray-700/50 rounded-lg`}>
                          <ActionIcon size={14} className={actionInfo.color} />
                          <span className="text-gray-300">{actionInfo.label}</span>
                        </div>
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-700/50 rounded-lg">
                          <Filter size={14} className="text-cyan-400" />
                          <span className="text-gray-300">{getFilterLabel(rule.targetFilter)}</span>
                        </div>
                        {rule.schedule?.enabled && (
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-700/50 rounded-lg">
                            <Clock size={14} className="text-yellow-400" />
                            <span className="text-gray-300">{(rule.schedule.days||[]).map(d => dayNames[d]).join(', ')} {rule.schedule.startHour}-{rule.schedule.endHour}</span>
                          </div>
                        )}
                        {rule.triggerCount > 0 && <span className="text-gray-500 text-xs">Executada {rule.triggerCount}x · Última: {new Date(rule.lastTriggeredAt).toLocaleString('pt-BR')}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => toggleActive(rule.id, rule.isActive)} className={`p-2 rounded-lg ${rule.isActive ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-400'} hover:opacity-80`}>{rule.isActive ? <Pause size={18} /> : <Play size={18} />}</button>
                      <button onClick={() => openEdit(rule)} className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg"><Edit3 size={18} /></button>
                      <button onClick={() => handleDelete(rule.id)} className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg"><Trash2 size={18} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">{editingRule ? 'Editar Regra' : 'Nova Regra de Automação'}</h3>
              <button onClick={resetForm} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-6">
              {/* Nome e Descrição */}
              <div className="space-y-3">
                <div><label className="text-gray-400 text-sm mb-1 block">Nome da Regra *</label><input type="text" value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white" placeholder="Ex: Pausar CPA Alto" /></div>
                <div><label className="text-gray-400 text-sm mb-1 block">Descrição</label><input type="text" value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white" placeholder="Ex: Pausa campanhas com CPA > R$200" /></div>
              </div>

              {/* Condição */}
              <div className="bg-gray-900/50 rounded-xl p-4">
                <label className="text-white text-sm font-medium mb-3 flex items-center gap-2"><AlertTriangle size={16} className="text-orange-400" />Condição</label>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <select value={formData.conditions.metric} onChange={(e) => setFormData(p => ({ ...p, conditions: { ...p.conditions, metric: e.target.value } }))} className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm">
                    {metrics.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  <select value={formData.conditions.operator} onChange={(e) => setFormData(p => ({ ...p, conditions: { ...p.conditions, operator: e.target.value } }))} className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm">
                    {operators.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <input type="number" value={formData.conditions.value} onChange={(e) => setFormData(p => ({ ...p, conditions: { ...p.conditions, value: e.target.value } }))} className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm" placeholder="Valor" />
                </div>
                <select value={formData.conditions.period} onChange={(e) => setFormData(p => ({ ...p, conditions: { ...p.conditions, period: e.target.value } }))} className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm">
                  {periods.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>

              {/* Filtro de Alvo */}
              <div className="bg-gray-900/50 rounded-xl p-4">
                <label className="text-white text-sm font-medium mb-3 flex items-center gap-2"><Filter size={16} className="text-cyan-400" />Filtro de Campanhas</label>
                <select value={formData.targetFilter.type} onChange={(e) => setFormData(p => ({ ...p, targetFilter: { ...p.targetFilter, type: e.target.value } }))} className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm mb-3">
                  {filterTypes.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
                {formData.targetFilter.type === 'name_contains' && (
                  <input type="text" value={formData.targetFilter.nameContains || ''} onChange={(e) => setFormData(p => ({ ...p, targetFilter: { ...p.targetFilter, nameContains: e.target.value } }))} className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm" placeholder="Palavra no nome da campanha" />
                )}
                {formData.targetFilter.type === 'specific' && (
                  <input type="text" value={formData.targetFilter.campaignId || ''} onChange={(e) => setFormData(p => ({ ...p, targetFilter: { ...p.targetFilter, campaignId: e.target.value } }))} className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm" placeholder="ID da campanha" />
                )}
              </div>

              {/* Ação */}
              <div className="bg-gray-900/50 rounded-xl p-4">
                <label className="text-white text-sm font-medium mb-3 flex items-center gap-2"><Zap size={16} className="text-yellow-400" />Ação</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
                  {actionTypes.map(action => (
                    <button type="button" key={action.value} onClick={() => setFormData(p => ({ ...p, actions: { ...p.actions, type: action.value } }))} className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${formData.actions.type === action.value ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-700 border-gray-600 text-gray-300 hover:border-gray-500'}`}>
                      <action.icon size={18} /><span className="text-sm">{action.label}</span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-4">
                  <div>
                    <label className="text-gray-500 text-xs mb-1 block">Nível de ação</label>
                    <select value={formData.actions.target || 'campaign'} onChange={(e) => setFormData(p => ({ ...p, actions: { ...p.actions, target: e.target.value } }))} className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm">
                      {targetLevels.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  {(formData.actions.type === 'increase_budget' || formData.actions.type === 'decrease_budget') && (
                    <div>
                      <label className="text-gray-500 text-xs mb-1 block">Percentual (%)</label>
                      <input type="number" value={formData.actions.budgetPercent || 20} onChange={(e) => setFormData(p => ({ ...p, actions: { ...p.actions, budgetPercent: parseInt(e.target.value) || 0 } }))} className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm w-24" min="1" max="100" />
                    </div>
                  )}
                  {(formData.actions.type === 'increase_budget_value' || formData.actions.type === 'decrease_budget_value') && (
                    <div>
                      <label className="text-gray-500 text-xs mb-1 block">Valor (R$)</label>
                      <input type="number" value={formData.actions.budgetValue || 0} onChange={(e) => setFormData(p => ({ ...p, actions: { ...p.actions, budgetValue: parseFloat(e.target.value) || 0 } }))} className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm w-32" min="0" step="0.01" placeholder="Ex: 50.00" />
                    </div>
                  )}
                  {formData.actions.type === 'set_budget' && (
                    <div>
                      <label className="text-gray-500 text-xs mb-1 block">Novo Orçamento (R$)</label>
                      <input type="number" value={formData.actions.budgetValue || 0} onChange={(e) => setFormData(p => ({ ...p, actions: { ...p.actions, budgetValue: parseFloat(e.target.value) || 0 } }))} className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm w-32" min="0" step="0.01" placeholder="Ex: 100.00" />
                    </div>
                  )}
                </div>
              </div>

              {/* Agendamento */}
              <div className="bg-gray-900/50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-white text-sm font-medium flex items-center gap-2"><Clock size={16} className="text-yellow-400" />Agendamento</label>
                  <button type="button" onClick={() => setFormData(p => ({ ...p, schedule: { ...p.schedule, enabled: !p.schedule.enabled } }))} className={`w-10 h-5 rounded-full transition-colors relative ${formData.schedule.enabled ? 'bg-blue-600' : 'bg-gray-600'}`}>
                    <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${formData.schedule.enabled ? 'right-0.5' : 'left-0.5'}`} />
                  </button>
                </div>
                {formData.schedule.enabled && (
                  <div className="space-y-3">
                    <div>
                      <label className="text-gray-500 text-xs mb-2 block">Dias da semana</label>
                      <div className="flex gap-1">
                        {dayNames.map((name, idx) => (
                          <button type="button" key={idx} onClick={() => toggleDay(idx)} className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${(formData.schedule.days || []).includes(idx) ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'}`}>{name}</button>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div><label className="text-gray-500 text-xs mb-1 block">Hora início</label><input type="time" value={formData.schedule.startHour || '00:00'} onChange={(e) => setFormData(p => ({ ...p, schedule: { ...p.schedule, startHour: e.target.value } }))} className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
                      <div><label className="text-gray-500 text-xs mb-1 block">Hora fim</label><input type="time" value={formData.schedule.endHour || '23:59'} onChange={(e) => setFormData(p => ({ ...p, schedule: { ...p.schedule, endHour: e.target.value } }))} className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Notificações */}
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={formData.actions.notifyEmail} onChange={(e) => setFormData(p => ({ ...p, actions: { ...p.actions, notifyEmail: e.target.checked } }))} className="w-4 h-4 rounded border-gray-600 text-blue-600" /><span className="text-gray-300 text-sm">Notificar por Email</span></label>
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={formData.actions.notifyPush} onChange={(e) => setFormData(p => ({ ...p, actions: { ...p.actions, notifyPush: e.target.checked } }))} className="w-4 h-4 rounded border-gray-600 text-blue-600" /><span className="text-gray-300 text-sm">Notificar por Push</span></label>
              </div>
            </div>
            <div className="p-6 border-t border-gray-700 flex gap-3">
              <button onClick={resetForm} className="flex-1 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-600">Cancelar</button>
              <button onClick={handleSave} disabled={!formData.name || !formData.conditions.value} className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-50">{editingRule ? 'Salvar Alterações' : 'Criar Regra'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Automations;
